// ===================================================================
// KAIXO - CRMAgenda
// ===================================================================
// Pestaña "Agenda" del CRM: pendientes y atrasadas del usuario.
// Compartido por web y móvil (responsive: una sola columna en móvil,
// más holgura en web).
//
// Buckets renderizados (vienen ya agrupados del backend):
//   - missed   (rojo, top): acciones que pasaron de fecha sin hacerse
//   - today    (acento celeste): acciones de hoy
//   - tomorrow / thisWeek / later: visualización ligera
//
// Acciones por card:
//   - Botón ✓ "Hecha" → marca completed:true
//   - Botón "📞 Llamar" si tiene phone (abre tel:)
//   - Click en cabecera → abre el lead en su detalle (callback al padre)
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { crmGetAgenda, crmCompleteAction } from '../../services/crmApi';
import { Icons } from '../Icons/Icons';
import ct from './i18n';
import { getSocket } from '../../services/socket';
import './CRMAgenda.css';

const LABELS = {
    español: {
        missed: 'No realizadas', today: 'Hoy', tomorrow: 'Mañana', thisWeek: 'Esta semana', later: 'Más adelante',
        empty: 'No tienes acciones pendientes', refresh: 'Actualizar', done: 'Hecha', call: 'Llamar',
        message: 'Mensaje', visit: 'Visita', email: 'Email', other: 'Otra', loading: 'Cargando...'
    },
    english: {
        missed: 'Missed', today: 'Today', tomorrow: 'Tomorrow', thisWeek: 'This week', later: 'Later',
        empty: 'No pending actions', refresh: 'Refresh', done: 'Done', call: 'Call',
        message: 'Message', visit: 'Visit', email: 'Email', other: 'Other', loading: 'Loading...'
    }
};
function getLang() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return u.language || 'español';
    } catch { return 'español'; }
}
function lx() { return LABELS[getLang()] || LABELS.español; }

function fmtTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}
function fmtDay(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    } catch { return ''; }
}

// Iconos por tipo de acción. Para los que no existen como SVG en
// Icons.js usamos un fallback de texto compacto.
const TYPE_ICONS = {
    call:    (size=14) => <Icons.Phone size={size} />,
    message: () => <span style={{ fontSize: 12 }}>💬</span>,
    visit:   () => <span style={{ fontSize: 12 }}>📍</span>,
    email:   () => <span style={{ fontSize: 12 }}>✉️</span>,
    other:   (size=14) => <Icons.Star size={size} />
};

function ActionCard({ lead, labels, isMissed, onComplete, onOpenLead }) {
    const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '—';
    const action = lead.nextAction || {};
    const typeKey = action.type || 'call';
    const typeIcon = (TYPE_ICONS[typeKey] || TYPE_ICONS.call)(14);
    const typeLabel = labels[typeKey] || labels.other;

    return (
        <div className={`crm-ag-card ${isMissed ? 'missed' : ''}`}>
            <div className="crm-ag-card-head" onClick={() => onOpenLead && onOpenLead(lead)}>
                <div className="crm-ag-name">{fullName}</div>
                <div className="crm-ag-when">
                    {fmtTime(action.at)}
                    {isMissed && <span className="crm-ag-day"> · {fmtDay(action.at)}</span>}
                </div>
            </div>
            <div className="crm-ag-line">
                <span className="crm-ag-type">{typeIcon} {typeLabel}</span>
                {action.note && <span className="crm-ag-note">{action.note}</span>}
            </div>
            <div className="crm-ag-actions">
                {lead.phone && typeKey === 'call' && (
                    <a href={`tel:${lead.phone}`} className="crm-ag-btn crm-ag-btn-call">
                        <Icons.Phone size={13} /> {labels.call}
                    </a>
                )}
                <button type="button" className="crm-ag-btn crm-ag-btn-done"
                        onClick={() => onComplete(lead._id)}>
                    <Icons.Check size={13} /> {labels.done}
                </button>
            </div>
        </div>
    );
}

export default function CRMAgenda({ onOpenLead }) {
    const [agenda, setAgenda] = useState({ missed: [], today: [], tomorrow: [], thisWeek: [], later: [] });
    const [loading, setLoading] = useState(true);
    const labels = lx();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await crmGetAgenda();
            setAgenda(data.agenda || { missed: [], today: [], tomorrow: [], thisWeek: [], later: [] });
        } catch (e) { console.warn('crmGetAgenda:', e?.message); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    // Realtime: si llega socket de cambio de lead, recarga la agenda.
    useEffect(() => {
        const s = typeof getSocket === 'function' ? getSocket() : null;
        if (!s) return;
        const handler = () => load();
        s.on('crm:lead_updated', handler);
        s.on('crm:lead_created', handler);
        s.on('crm:lead_deleted', handler);
        s.on('crm:agenda_reminder', handler);
        return () => {
            try {
                s.off('crm:lead_updated', handler);
                s.off('crm:lead_created', handler);
                s.off('crm:lead_deleted', handler);
                s.off('crm:agenda_reminder', handler);
            } catch {}
        };
    }, [load]);

    const handleComplete = async (leadId) => {
        // Optimistic UI: quitar de los buckets al instante.
        setAgenda(prev => {
            const next = {};
            for (const k of Object.keys(prev)) {
                next[k] = (prev[k] || []).filter(l => String(l._id) !== String(leadId));
            }
            return next;
        });
        try {
            await crmCompleteAction(leadId);
        } catch (e) {
            console.warn('completeAction:', e?.message);
            load(); // recovery
        }
    };

    const total = ['missed','today','tomorrow','thisWeek','later']
        .reduce((a, k) => a + (agenda[k]?.length || 0), 0);

    if (loading && total === 0) {
        return <div className="crm-ag-empty">{labels.loading}</div>;
    }
    if (total === 0) {
        return <div className="crm-ag-empty">{labels.empty}</div>;
    }

    const renderBucket = (key, isMissed = false) => {
        const items = agenda[key] || [];
        if (items.length === 0) return null;
        return (
            <section className={`crm-ag-section ${isMissed ? 'sec-missed' : ''} sec-${key}`}>
                <h3 className="crm-ag-section-head">
                    <span>{labels[key]}</span>
                    <span className="crm-ag-count">{items.length}</span>
                </h3>
                <div className="crm-ag-list">
                    {items.map(l => (
                        <ActionCard
                            key={l._id}
                            lead={l}
                            labels={labels}
                            isMissed={isMissed}
                            onComplete={handleComplete}
                            onOpenLead={onOpenLead}
                        />
                    ))}
                </div>
            </section>
        );
    };

    return (
        <div className="crm-ag-root">
            <div className="crm-ag-toolbar">
                <span className="crm-ag-meta">{ct.total}: <strong>{total}</strong></span>
                <button type="button" className="crm-ag-refresh" onClick={load} disabled={loading}>
                    {loading ? '…' : '↻'} {labels.refresh}
                </button>
            </div>
            {renderBucket('missed', true)}
            {renderBucket('today')}
            {renderBucket('tomorrow')}
            {renderBucket('thisWeek')}
            {renderBucket('later')}
        </div>
    );
}
